import React, { useMemo, useState } from "react";
import {
  Checkbox,
  Content,
  Dialog,
  Flex,
  Heading,
  Item,
  NumberField,
  Radio,
  RadioGroup,
  Text,
  TextArea,
  View,
  DatePicker,
  ComboBox
} from "@adobe/react-spectrum";
import { DateValue, fromDate, getLocalTimeZone } from "@internationalized/date";
import { Button } from "react-bootstrap";
import styles from "./event_recurring.module.css";
import { useTranslation } from "react-i18next";

/* ------------------------- TYPES ------------------------- */

type RepeatMode = "DAY" | "WEEK" | "MONTH" | "YEAR";

interface RepeatApi {
  repeatMode: string;
  validFrom: string;
  validTo: string;
  repeatEvery: string;
  repeatDay: string;
  repeatMonth: string;
  repeatWeekDay: string;
  repeatWeekCount: string;
  repeatWeekDayList: string;
  timeFrom: string;
  timeTo: string;
  offset: string;
  limit: string;
}

interface RecurrenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  repeat: RepeatApi | null;
  onSave: (repeat: RepeatApi) => void;
  timeFrom: string;
  timeTo: string;
}

/* ------------------------- CONSTANTS ------------------------- */

const API_TO_MODE: Record<string, RepeatMode> = {
  "1": "DAY",
  "2": "WEEK",
  "3": "MONTH",
  "4": "YEAR"
};

const MODE_TO_API: Record<RepeatMode, string> = {
  DAY: "1",
  WEEK: "2",
  MONTH: "3",
  YEAR: "4"
};

// Day dropdown: first blank option (0), then 1–31
const DAY_OF_MONTH_OPTIONS = [
  { id: 0, label: "" },
  ...Array.from({ length: 31 }, (_, i) => ({
    id: i + 1,
    label: String(i + 1)
  }))
];

/* ------------------------- HELPERS ------------------------- */

function parseDate(s?: string): DateValue | null {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return fromDate(d, getLocalTimeZone());
}

function formatDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/* ---------------------- MAIN MODAL ------------------------ */

export default function RecurrenceModal({
  isOpen,
  onClose,
  repeat,
  onSave,
  timeFrom,
  timeTo
}: RecurrenceModalProps) {
  const { t } = useTranslation("calendar");
  const _t = (key: string) => t(`recurrence_form.${key}`, { ns: "calendar" });

  /* ---------- OPTIONS (TRANSLATED) ---------- */

  const WEEKDAY_OPTIONS = useMemo(
    () => [
      { id: 1, label: _t("monday") },
      { id: 2, label: _t("tuesday") },
      { id: 3, label: _t("wednesday") },
      { id: 4, label: _t("thursday") },
      { id: 5, label: _t("friday") },
      { id: 6, label: _t("saturday") },
      { id: 7, label: _t("sunday") }
    ],
    [t] // re-run on language change
  );

  const WEEK_NUMBER_OPTIONS = useMemo(
    () => [
      { id: 1, label: _t("ist_week") },
      { id: 2, label: _t("2nd_week") },
      { id: 3, label: _t("3rd_week") },
      { id: 4, label: _t("4th_week") },
      { id: 5, label: _t("last_week") }
    ],
    [t]
  );

  const MONTH_OPTIONS = useMemo(
    () => [
      { id: 1, label: _t("january") },
      { id: 2, label: _t("february") },
      { id: 3, label: _t("march") },
      { id: 4, label: _t("april") },
      { id: 5, label: _t("may") },
      { id: 6, label: _t("june") },
      { id: 7, label: _t("july") },
      { id: 8, label: _t("august") },
      { id: 9, label: _t("september") },
      { id: 10, label: _t("october") },
      { id: 11, label: _t("november") },
      { id: 12, label: _t("december") }
    ],
    [t]
  );

  /* ---------- INITIAL VALUES ---------- */

  const initialMode: RepeatMode = repeat ? API_TO_MODE[repeat.repeatMode] : "WEEK";

  const [mode, setMode] = useState<RepeatMode>(initialMode);
  const [every, setEvery] = useState<number>(repeat ? Number(repeat.repeatEvery) || 1 : 1);

  const [weekDays, setWeekDays] = useState<number[]>(
    repeat?.repeatWeekDayList
      ? repeat.repeatWeekDayList
          .split(",")
          .map((n) => Number(n))
          .filter((n) => !Number.isNaN(n))
      : []
  );

  // dayOfMonth: 0 means "no specific day selected" (blank in dropdown)
  const [dayOfMonth, setDayOfMonth] = useState<number>(() => {
    if (repeat && repeat.repeatDay !== undefined && repeat.repeatDay !== null) {
      const n = Number(repeat.repeatDay);
      return Number.isNaN(n) ? 0 : n;
    }
    return 0; // new entry → blank
  });

  const [weekNumber, setWeekNumber] = useState<number>(repeat?.repeatWeekCount ? Number(repeat.repeatWeekCount) || 0 : 0);

  const [dayOfWeek, setDayOfWeek] = useState<number>(repeat?.repeatWeekDay ? Number(repeat.repeatWeekDay) || 0 : 0);

  const [monthOfYear, setMonthOfYear] = useState<number>(repeat?.repeatMonth ? Number(repeat.repeatMonth) || 1 : 1);

  const [validFrom, setValidFrom] = useState<DateValue | null>(parseDate(repeat?.validFrom));
  const [validTo, setValidTo] = useState<DateValue | null>(parseDate(repeat?.validTo));

  /* ---------- MUTUAL DISABLING LOGIC (MONTH MODE) ---------- */

  // If user selects day-of-week or week, we disable Day dropdown
  const disableDay = dayOfWeek !== 0 || weekNumber !== 0;
  // If user selects a specific day-of-month (non-zero), disable the other two
  const disableWeekControls = dayOfMonth !== 0;

  /* ---------- SUMMARY TEXT ---------- */

  const summary = useMemo(() => {
    const tz = getLocalTimeZone();
    const vf = validFrom ? formatDateOnly(validFrom.toDate(tz)) : "";
    const vt = validTo ? formatDateOnly(validTo.toDate(tz)) : "";

    let text = "";

    if (mode === "DAY") {
      text = `${_t("every")} ${every} ${_t("days")}.`;
    }

    if (mode === "WEEK") {
      const labels = WEEKDAY_OPTIONS.filter((w) => weekDays.includes(w.id))
        .map((w) => w.label)
        .join(", ");
      text = `${_t("every")} ${every} ${_t("week")} (${labels || "-"})`;
    }

    if (mode === "MONTH") {
      if (dayOfMonth !== 0) {
        text = `${_t("every")} ${every} ${_t("month")} - ${_t("day_of_month")}: ${dayOfMonth}.`;
      } else {
        const weekText = WEEK_NUMBER_OPTIONS.find((x) => x.id === weekNumber)?.label || "";
        const dayLabel = WEEKDAY_OPTIONS.find((x) => x.id === dayOfWeek)?.label || "";
        const pattern = `${weekText} ${dayLabel}`.trim();
        text = `${_t("every")} ${every} ${_t("month")}${pattern ? ` - ${pattern}` : ""}`;
      }
    }

    if (mode === "YEAR") {
      const monthLabel = MONTH_OPTIONS.find((x) => x.id === monthOfYear)?.label || "";
      if (dayOfMonth === 0) {
        text = `${_t("every")} ${every} ${_t("year")} - ${monthLabel}`;
      } else {
        text = `${_t("every")} ${every} ${_t("year")} - ${dayOfMonth} ${monthLabel}`;
      }
    }

    text += ` | ${_t("start_date")}: ${vf} | ${_t("end_date")}: ${vt}`;
    text += ` | ${timeFrom} - ${timeTo}`;

    return text;
  }, [
    mode,
    every,
    weekDays,
    dayOfMonth,
    weekNumber,
    dayOfWeek,
    monthOfYear,
    validFrom,
    validTo,
    timeFrom,
    timeTo,
    WEEKDAY_OPTIONS,
    WEEK_NUMBER_OPTIONS,
    MONTH_OPTIONS,
    t
  ]);

  /* ---------- SAVE ---------- */

  const handleOK = () => {
    const tz = getLocalTimeZone();
    const vf = validFrom?.toDate(tz) ?? new Date();
    const vt = validTo?.toDate(tz) ?? new Date();

    const obj: RepeatApi = {
      repeatMode: MODE_TO_API[mode],
      validFrom: `${formatDateOnly(vf)}T00:00:00`,
      validTo: `${formatDateOnly(vt)}T00:00:00`,
      repeatEvery: String(every),
      repeatDay: mode === "MONTH" || mode === "YEAR" ? String(dayOfMonth) : "0",
      repeatMonth: mode === "YEAR" ? String(monthOfYear) : "0",
      repeatWeekDay: mode === "MONTH" ? String(dayOfWeek) : "0",
      repeatWeekCount: mode === "MONTH" ? String(weekNumber) : "0",
      repeatWeekDayList: mode === "WEEK" ? weekDays.sort((a, b) => a - b).join(",") : "0",
      timeFrom,
      timeTo,
      offset: "0",
      limit: "1"
    };

    onSave(obj);
    onClose();
  };

  if (!isOpen) return null;

  /* ------------------------- UI ------------------------- */

  return (
    <Dialog width="520px" isDismissable onDismiss={onClose}>
      <Heading>{_t("edit_recurrence")}</Heading>

      <Content>
        <Flex direction="column" gap="size-200">
          {/* EVERY + MODE */}
          <NumberField
            label={_t("every")}
            labelPosition="top"
            minValue={1}
            value={every}
            onChange={(v) => setEvery(v ?? 1)}
            width="100%"
          />

          <RadioGroup value={mode} onChange={(v) => setMode(v as RepeatMode)} orientation="horizontal">
            <Radio value="DAY">{_t("day")}</Radio>
            <Radio value="WEEK">{_t("week")}</Radio>
            <Radio value="MONTH">{_t("month")}</Radio>
            <Radio value="YEAR">{_t("year")}</Radio>
          </RadioGroup>

          {/* MODE = WEEK */}
          {mode === "WEEK" && (
            <View>
              <Text>{_t("choose_week_days")}</Text>
              <Flex direction="row" wrap gap="size-100" marginTop="size-100">
                {WEEKDAY_OPTIONS.map((w) => (
                  <Checkbox
                    key={w.id}
                    isSelected={weekDays.includes(w.id)}
                    onChange={(sel) => {
                      setWeekDays((prev) => {
                        if (sel) return [...prev, w.id];
                        return prev.filter((x) => x !== w.id);
                      });
                    }}
                  >
                    {w.label}
                  </Checkbox>
                ))}
              </Flex>
            </View>
          )}

          {/* MODE = MONTH */}
          {mode === "MONTH" && (
            <View>
              <Flex direction="column" gap="size-200" marginTop="size-200">
                {/* Day dropdown */}
                <ComboBox
                  label={_t("day")}
                  labelPosition="top"
                  width="100%"
                  items={DAY_OF_MONTH_OPTIONS}
                  selectedKey={String(dayOfMonth)}
                  onSelectionChange={(key) => {
                    const num = key ? Number(key) : 0;
                    setDayOfMonth(num);
                    if (num !== 0) {
                      setDayOfWeek(0);
                      setWeekNumber(0);
                    }
                  }}
                  isDisabled={disableDay}
                >
                  {(item: any) => <Item key={String(item.id)}>{item.label}</Item>}
                </ComboBox>

                {/* Day of Week dropdown */}
                <ComboBox
                  label={_t("day_of_week")}
                  labelPosition="top"
                  width="100%"
                  items={WEEKDAY_OPTIONS}
                  selectedKey={dayOfWeek ? String(dayOfWeek) : undefined}
                  onSelectionChange={(key) => {
                    const num = key ? Number(key) : 0;
                    setDayOfWeek(num);
                    setDayOfMonth(0);
                  }}
                  isDisabled={disableWeekControls}
                >
                  {(item: any) => <Item key={String(item.id)}>{item.label}</Item>}
                </ComboBox>

                {/* Week number dropdown */}
                <ComboBox
                  label={_t("week")}
                  labelPosition="top"
                  width="100%"
                  items={WEEK_NUMBER_OPTIONS}
                  selectedKey={weekNumber ? String(weekNumber) : undefined}
                  onSelectionChange={(key) => {
                    const num = key ? Number(key) : 0;
                    setWeekNumber(num);
                    setDayOfMonth(0);
                  }}
                  isDisabled={disableWeekControls}
                >
                  {(item: any) => <Item key={String(item.id)}>{item.label}</Item>}
                </ComboBox>
              </Flex>
            </View>
          )}

          {/* MODE = YEAR */}
          {mode === "YEAR" && (
            <Flex direction="column" gap="size-200">
              <ComboBox
                label={_t("day")}
                labelPosition="top"
                width="100%"
                items={DAY_OF_MONTH_OPTIONS}
                selectedKey={String(dayOfMonth)}
                onSelectionChange={(key) => {
                  const num = key ? Number(key) : 0;
                  setDayOfMonth(num);
                }}
              >
                {(item: any) => <Item key={String(item.id)}>{item.label}</Item>}
              </ComboBox>

              <ComboBox
                label={_t("month")}
                width="100%"
                items={MONTH_OPTIONS}
                selectedKey={String(monthOfYear)}
                onSelectionChange={(key) => setMonthOfYear(key ? Number(key) : 1)}
              >
                {(item: any) => <Item key={String(item.id)}>{item.label}</Item>}
              </ComboBox>
            </Flex>
          )}

          {/* RANGE */}
          <View>
            <Text>{_t("recurrence_range")}</Text>
            <Flex direction="row" gap="size-200" marginTop="size-200">
              <DatePicker
                width={"50%"}
                label={_t("start_date")}
                value={validFrom ?? undefined}
                onChange={setValidFrom}
                granularity="day"
              />
              <DatePicker
                width={"50%"}
                label={_t("end_date")}
                value={validTo ?? undefined}
                onChange={setValidTo}
                granularity="day"
              />
            </Flex>
          </View>

          {/* SUMMARY */}
          <TextArea width={"100%"} value={summary} isReadOnly height="size-2400" />

          {/* BUTTONS */}
          <Flex justifyContent={"end"} gap={"size-200"} marginTop={"size-200"}>
            <Flex gap={"size-200"} UNSAFE_className={styles.button_container}>
              <Button variant="danger" onClick={onClose} className={`${styles.btn} ${styles.btn_cancel}`}>
                {_t("cancel") ?? "Cancel"}
              </Button>
              <Button type="submit" onClick={handleOK} className={`${styles.btn} ${styles["btn-add"]}`}>
                {_t("ok") ?? "Ok"}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Content>
    </Dialog>
  );
}
